/**
 * Customer Modal Component - Using FormModal with Unsaved Changes Detection
 * Includes: Basic Info, Addresses, Contacts
 */
import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { PhoneInput, CpfInput } from '../../components/form/MaskedInputs';
import { User, MapPin, Phone, Plus, Trash2, Search, Star, Loader2 } from 'lucide-react';

const addressSchema = z.object({
  label: z.string().min(1, 'Label obrigatório'),
  zip_code: z.string().min(8, 'CEP inválido'),
  street: z.string().min(1, 'Rua obrigatória'),
  number: z.string().min(1, 'Número obrigatório'),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, 'Bairro obrigatório'),
  city: z.string().min(1, 'Cidade obrigatória'),
  state: z.string().min(2, 'Estado obrigatório').max(2, 'Use a sigla do estado'),
  is_primary: z.boolean().optional(),
});

const contactSchema = z.object({
  type: z.enum(['phone', 'email', 'whatsapp', 'other']),
  label: z.string().min(1, 'Label obrigatório'),
  value: z.string().min(1, 'Contato obrigatório'),
  is_primary: z.boolean().optional(),
});

const customerSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo'),
  document: z.string().optional(),
  notes: z.string().max(500, 'Observação muito longa').optional(),
  birthday: z.string().optional(),
  is_active: z.boolean().optional(),
  addresses: z.array(addressSchema).optional(),
  contacts: z.array(contactSchema).optional(),
});

export function CustomerModal({ open, onOpenChange, customer, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!customer?.id;
  const [activeTab, setActiveTab] = useState('basic');
  const [searchingCep, setSearchingCep] = useState({});

  const { register, handleSubmit, control, reset, setValue, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      document: '',
      notes: '',
      birthday: '',
      is_active: true,
      addresses: [],
      contacts: [],
    },
  });

  const { fields: addresses, append: appendAddress, remove: removeAddress, update: updateAddress } = useFieldArray({
    control,
    name: 'addresses',
  });

  const { fields: contacts, append: appendContact, remove: removeContact, update: updateContact } = useFieldArray({
    control,
    name: 'contacts',
  });

  // Reset form when modal opens or customer changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (customer) {
      reset({
        name: customer.name || '',
        document: customer.document || '',
        notes: customer.notes || '',
        birthday: customer.birthday || '',
        is_active: customer.is_active ?? true,
        addresses: customer.addresses || [],
        contacts: customer.contacts || [],
      });
    } else {
      reset({
        name: '',
        document: '',
        notes: '',
        birthday: '',
        is_active: true,
        addresses: [],
        contacts: [],
      });
    }
    setActiveTab('basic');
  }, [open, customer, reset]);

  // CEP lookup function
  const lookupCep = async (index, cep) => {
    if (!cep || cep.replace(/\D/g, '').length < 8) return;
    
    setSearchingCep(prev => ({ ...prev, [index]: true }));
    
    try {
      const cleanCep = cep.replace(/\D/g, '');
      const res = await api.get(`/utils/cep/${cleanCep}`);
      
      if (res.data) {
        const currentAddress = addresses[index];
        updateAddress(index, {
          ...currentAddress,
          zip_code: res.data.zip_code || cep,
          street: res.data.street || '',
          neighborhood: res.data.neighborhood || '',
          city: res.data.city || '',
          state: res.data.state || '',
          complement: res.data.complement || currentAddress?.complement || '',
        });
        toast.success('Endereço encontrado!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CEP não encontrado');
    } finally {
      setSearchingCep(prev => ({ ...prev, [index]: false }));
    }
  };

  // Set address as primary
  const setAddressPrimary = (index) => {
    addresses.forEach((addr, i) => {
      updateAddress(i, { ...addr, is_primary: i === index });
    });
  };

  // Set contact as primary for its type
  const setContactPrimary = (index) => {
    const targetType = contacts[index].type;
    contacts.forEach((contact, i) => {
      if (contact.type === targetType) {
        updateContact(i, { ...contact, is_primary: i === index });
      }
    });
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const addNewAddress = () => {
    appendAddress({
      label: addresses.length === 0 ? 'Casa' : '',
      zip_code: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      is_primary: addresses.length === 0,
    });
  };

  const addNewContact = (type = 'phone') => {
    const existingOfType = contacts.filter(c => c.type === type);
    appendContact({
      type,
      label: existingOfType.length === 0 ? 'Pessoal' : '',
      value: '',
      is_primary: existingOfType.length === 0,
    });
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('customers.editCustomer') : t('customers.newCustomer')}
      description={isEditing ? 'Atualize os dados do cliente' : 'Preencha os dados do novo cliente'}
      size="lg"
      formId="customer"
      entityId={customer?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Dados
          </TabsTrigger>
          <TabsTrigger value="addresses" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Endereços
            {addresses.length > 0 && <Badge variant="secondary" className="ml-1">{addresses.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Contatos
            {contacts.length > 0 && <Badge variant="secondary" className="ml-1">{contacts.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('customers.name')} *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Nome do cliente"
              data-testid="customer-name-input"
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="document">{t('customers.cpf')}</Label>
              <Controller
                name="document"
                control={control}
                render={({ field }) => (
                  <CpfInput
                    id="document"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthday">Data de Nascimento</Label>
              <Input
                id="birthday"
                type="date"
                {...register('birthday')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('customers.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Observações sobre o cliente"
              rows={3}
            />
          </div>

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="is_active">{t('common.active')}</Label>
              </div>
            )}
          />
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {addresses.length === 0 ? 'Nenhum endereço cadastrado' : `${addresses.length} endereço(s)`}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={addNewAddress}>
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>

          {addresses.length === 0 && (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <MapPin className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Clique em "Adicionar" para cadastrar endereços</p>
            </div>
          )}

          {addresses.map((address, index) => (
            <div key={address.id || index} className="p-4 border rounded-lg space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Input
                    {...register(`addresses.${index}.label`)}
                    placeholder="Label (Casa, Trabalho...)"
                    className="w-40 h-8"
                  />
                  {address.is_primary && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                      <Star className="h-4 w-4 text-primary fill-primary" />
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  {!address.is_primary && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setAddressPrimary(index)}
                      title="Definir como principal"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeAddress(index)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">CEP</Label>
                  <div className="flex gap-1">
                    <Input
                      {...register(`addresses.${index}.zip_code`)}
                      placeholder="00000-000"
                      className="h-9"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => lookupCep(index, watch(`addresses.${index}.zip_code`))}
                      disabled={searchingCep[index]}
                    >
                      {searchingCep[index] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Rua</Label>
                  <Input {...register(`addresses.${index}.street`)} placeholder="Rua" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Número</Label>
                  <Input {...register(`addresses.${index}.number`)} placeholder="Nº" className="h-9" />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Complemento</Label>
                  <Input {...register(`addresses.${index}.complement`)} placeholder="Apto, Sala..." className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Bairro</Label>
                  <Input {...register(`addresses.${index}.neighborhood`)} placeholder="Bairro" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cidade</Label>
                  <Input {...register(`addresses.${index}.city`)} placeholder="Cidade" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">UF</Label>
                  <Input {...register(`addresses.${index}.state`)} placeholder="UF" maxLength={2} className="h-9" />
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {contacts.length === 0 ? 'Nenhum contato cadastrado' : `${contacts.length} contato(s)`}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addNewContact('phone')}>
                <Plus className="h-4 w-4 mr-1" />
                Telefone
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addNewContact('email')}>
                <Plus className="h-4 w-4 mr-1" />
                Email
              </Button>
            </div>
          </div>

          {contacts.length === 0 && (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <Phone className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Clique em "Telefone" ou "Email" para adicionar contatos</p>
            </div>
          )}

          {contacts.map((contact, index) => (
            <div key={contact.id || index} className="p-3 border rounded-lg flex items-center gap-3 bg-muted/30">
              <Controller
                name={`contacts.${index}.type`}
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-28 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              
              <Input
                {...register(`contacts.${index}.label`)}
                placeholder="Label (Pessoal, Comercial...)"
                className="w-32 h-9"
              />

              {watch(`contacts.${index}.type`) === 'phone' || watch(`contacts.${index}.type`) === 'whatsapp' ? (
                <Controller
                  name={`contacts.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      className="flex-1 h-9"
                    />
                  )}
                />
              ) : (
                <Input
                  {...register(`contacts.${index}.value`)}
                  placeholder={watch(`contacts.${index}.type`) === 'email' ? 'email@exemplo.com' : 'Contato'}
                  type={watch(`contacts.${index}.type`) === 'email' ? 'email' : 'text'}
                  className="flex-1 h-9"
                />
              )}

              {contact.is_primary && (
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                  <Star className="h-4 w-4 text-primary fill-primary" />
                </div>
              )}
              
              {!contact.is_primary && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => setContactPrimary(index)}
                  title="Definir como principal"
                >
                  <Star className="h-4 w-4" />
                </Button>
              )}
              
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeContact(index)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </FormModal>
  );
}

export default CustomerModal;
